import { Component, inject } from '@angular/core';
import { UserService } from '../../../services/user.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { SoftphoneStateDto, UserInterface } from 'voxr-shared';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { AppService, ThemePreference } from '../../../services/app.service';
import { UserAvatarComponent } from "../user-avatar/user-avatar.component";
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { SoftphoneService, SoftphoneState } from '../../../services/softphone.service';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { WorkspaceService } from '../../../services/workspace.service';
import { firstValueFrom } from 'rxjs';
import { LiveTransferPositionComponent } from '../live-transfer-position/live-transfer-position.component';
import { PulseLoginService } from '../../../services/pulse-login.service';

type SoftphoneLauncherTone = 'success' | 'warning' | 'danger' | 'secondary';

@Component({
  selector: 'app-user-status',
  standalone: true,
  imports: [
    CommonModule, FormsModule, RouterLink,
    MatButtonModule, MatIconModule, MatSelectModule, MatMenuModule, MatDividerModule, MatButtonToggleModule, MatTooltipModule,
    UserAvatarComponent, LiveTransferPositionComponent],
  templateUrl: './user-status.component.html',
  styleUrl: './user-status.component.scss'
})
export class UserStatusComponent {
  private app = inject(AppService);
  private userService = inject(UserService);
  private softphoneService = inject(SoftphoneService);
  private workspaceService = inject(WorkspaceService);
  pulseLogin = inject(PulseLoginService);

  user?: UserInterface & { _impersonated?: boolean } | null;
  softphoneState?: SoftphoneStateDto;
  private softphoneRuntimeState: SoftphoneState = 'inactive';
  private softphoneRuntimeDetail?: string = 'Soft phone is off';
  loading = true;
  theme = this.app.theme;
  hasWorkspaces = false;
  softphoneLauncherTone: SoftphoneLauncherTone = 'secondary';
  softphoneLauncherTooltip = 'Soft phone is off';
  softphoneLauncherDisabled = true;
  softphoneLauncherNeedsAttention = false;
  themeOptions: { value: ThemePreference; label: string; icon: string; }[] = [
    { value: 'default', label: 'Default', icon: 'contrast' },
    { value: 'light', label: 'Light', icon: 'light_mode' },
    { value: 'dark', label: 'Dark', icon: 'dark_mode' },
  ];

  constructor() {
    this.userService.user$.pipe(takeUntilDestroyed()).subscribe(async user => await this.load(user));
    this.userService.softphoneStateDto$.pipe(takeUntilDestroyed()).subscribe(state => {
      this.softphoneState = state;
      this.syncSoftphoneLauncherState();
    });
    this.softphoneService.softphoneState$.pipe(takeUntilDestroyed()).subscribe(state => {
      this.softphoneRuntimeState = state;
      this.syncSoftphoneLauncherState();
    });
    this.softphoneService.softphoneStateDetail$.pipe(takeUntilDestroyed()).subscribe(detail => {
      this.softphoneRuntimeDetail = detail;
      this.syncSoftphoneLauncherState();
    });
  }

  private async load(user?: UserInterface) {
    this.user = user;
    if (this.user) {
      this.app.loadUserTheme(this.user._id!.toString());

      const workspaces = await firstValueFrom(this.workspaceService.workspaces$);
      this.hasWorkspaces = !!workspaces?.length;
      if (workspaces?.length) {
        this.hasInvitations = workspaces.some(ws => ws.invitations?.some(inv => inv.email?.toLowerCase() == this.user!.email!.toLowerCase() && !inv.accepted));
      }
    } else {
      this.hasInvitations = false;
      this.hasWorkspaces = false;
      this.app.loadUserTheme();
    }
    this.loading = false;
  }
  hasInvitations?: boolean;

  setTheme(theme: ThemePreference) {
    if (!this.user?._id) return;
    this.app.setTheme(theme, this.user._id.toString());
  }

  logout() { this.app.logout(); }
  openSoftphonePanel() {
    if (this.softphoneLauncherDisabled) return;
    this.softphoneService.requestSoftphonePanelOpen('app-shell');
  }
  unImpersonate() { this.userService.unImpersonate(); }
  impersonate(accountId: string) { this.userService.impersonate(accountId, false, false); }

  private syncSoftphoneLauncherState() {
    const enabled = !!this.softphoneState?.enabled;
    this.softphoneLauncherDisabled = !enabled;
    this.softphoneLauncherNeedsAttention = enabled
      && (this.softphoneRuntimeState === 'inactive' || this.softphoneRuntimeState === 'error');

    if (!enabled) {
      this.softphoneLauncherTooltip = 'Soft phone is disabled for this user.';
    } else if (this.softphoneRuntimeState === 'error') {
      this.softphoneLauncherTooltip = `Transfers will not ring in this browser. ${this.softphoneRuntimeDetail || 'Open the soft phone for details.'}`;
    } else if (this.softphoneRuntimeState === 'inactive' && this.softphoneRuntimeDetail?.toLowerCase().includes('another browser')) {
      this.softphoneLauncherTooltip = 'Transfers will not ring in this browser. Soft phone is active in another browser.';
    } else if (this.softphoneRuntimeState === 'inactive') {
      this.softphoneLauncherTooltip = 'Transfers will not ring while the soft phone is unavailable. Open for details.';
    } else {
      this.softphoneLauncherTooltip = this.softphoneRuntimeDetail || 'Soft phone state unavailable.';
    }

    if (!enabled) {
      this.softphoneLauncherTone = 'secondary';
      return;
    }

    switch (this.softphoneRuntimeState) {
      case 'ready':
        this.softphoneLauncherTone = 'success';
        return;
      case 'loading':
        this.softphoneLauncherTone = 'warning';
        return;
      case 'error':
        this.softphoneLauncherTone = 'danger';
        return;
      default:
        this.softphoneLauncherTone = 'warning';
        return;
    }
  }
}
